import { normPlace, normPlaces, type PlaceKind, type PlacesView, type PlaceView } from "./world";
// The API client. Every route the web uses (docs/product/spec.md, product/api) goes through `api`.
// `npm run dev:mock` (VITE_MOCK=1) installs the in-memory implementation from src/mock.ts instead.
import { normComparison } from "./compare"; // v4 (V4-MC)
import { normPersona, type Persona, type PersonaChange } from "./directors";
import { normProjectFiles } from "./download"; // V4-DL
import { normNextStep, type NextStepData } from "./nextstep";
import { normReferences, normRefUse } from "./references"; // v4 (V4-R2)
import { normPreviewEvent } from "./notes"; // v4 (T4)
import { normSuno, type SunoPatch, type SunoState, type SunoWriteArgs } from "./suno";
import { normPrices, type Prices } from "./price"; // PRICE-FIRST
import { docMime } from "./documents"; // 2026-11-03
import type {
  SceneFit,
  SceneFitInfo,
  DocumentAdded,
  WebsiteRead,
  AccessRequest,
  ProjectFiles,
  AdBrief,
  AdminCosts,
  Asset,
  AssetKind,
  Brand,
  Channel,
  CostKind,
  CostWindow,
  CreditPack,
  DirectorSession,
  Estimate,
  EstimateLine,
  Invite,
  JobErrorCode,
  JobKind,
  JobStatus,
  JobView,
  LedgerEntry,
  Me,
  ModelInfo,
  ModelStep,
  MusicBed,
  MyModels,
  Packs,
  PeopleList,
  Person,
  PersonGrant,
  PersonLedgerEntry,
  ProduceMode,
  ReferencesView,
  SetReferencesBody,
  Project,
  Render,
  RenderStatus,
  SfxEdit,
  SceneSoundPreview,
  SoundMix,
  LinesEdit,
  VoiceAudio,
  VoicePass,
  VoicePlan,
  Shot,
  StudioEvent,
  ToolResponse,
  User,
  Version,
  Memory,
  MemoryKind,
  VoiceInfo,
} from "./types";
import { FORMAT_GROUPS, isFormatId, type Character, type FormatInfo, type VideoBriefDraft } from "./formats";
import { noteErrorCode } from "./analytics";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** the api's error code ("NO_CREDITS", "DIRECTOR_DAILY_LIMIT"...), when it sends one */
    readonly code: string | null = null,
    /** the api's error details ({usedBytes, quotaBytes}, {maxBytes}, {maxSeconds, durationS}...), when it sends them */
    readonly details: unknown = null,
  ) {
    super(message);
    noteErrorCode(code, status); // 4.0.1 PostHog: an error toast that follows reports this code
  }
}

/** v3: `format` omitted (or "ad") is an ad exactly as before; any other format sends a VideoBriefDraft as `brief`. */
export type NewProject = { title: string; channelId?: string | null; /** v4: start it inside a collection */ collectionId?: string | null; brief?: Partial<AdBrief> | VideoBriefDraft; category?: string; template?: string; format?: string };
export type CharacterPatch = { name?: string; look?: string; voice?: string; voiceId?: string };
export type LibraryQuery = {
  kind?: AssetKind | "";
  q?: string;
  channelId?: string;
  projectId?: string;
  /** v4 (V4-A1/A2): "account" | "channel:<id>" | "collection:<id>" | "project:<id>"; here = only that level itself */
  scope?: string;
  here?: boolean;
  role?: string;
  origin?: string;
};
/** v4: PATCH /api/library/:id: rename, your tags, a role, or move it to another level (a scope string). */
export type LibraryPatch = { name?: string; tags?: string[]; role?: string | null; scope?: string };
/** POST library: rename/retag with `id`, or save a new item; pictures and logos upload as base64 (PNG/JPEG/WebP). */
export type SaveItem = {
  id?: string;
  kind?: AssetKind;
  name?: string;
  tags?: string[];
  meta?: Record<string, unknown>;
  channelId?: string | null;
  projectId?: string | null;
  mime?: string;
  dataBase64?: string;
};

/** v4 (V4-A1 pipeline, used by V4-M1): what a file is for and where it lives. */
export type UploadOpts = {
  kind: "image" | "logo" | "music" | "sfx" | "video";
  role?: string;
  /** "suno": a song the user made in Suno for this video */
  origin?: "upload" | "suno";
  scope?: { channelId?: string; collectionId?: string; projectId?: string };
  /** the one-time "I own these files" checkbox (UPLOAD_TERMS_CONSENT) */
  termsAccepted?: boolean;
};
export type UploadKindLimit = { mimes: string[]; maxBytes: number; maxSeconds?: number };
/** GET /api/me/storage. `usedBytes` counts against `quotaBytes` (uploads only; null = no limit); `limits` per kind. */
export type StorageInfo = {
  termsAccepted: boolean;
  usedBytes: number;
  quotaBytes: number | null;
  uploadsBytes?: number;
  generatedBytes?: number;
  byKind?: Record<string, number>;
  limits?: Partial<Record<UploadOpts["kind"], UploadKindLimit>>;
};
export type ThumbnailResult = { version: number; thumbUrl: string | null; posterAtS: number | null; posterCustom: boolean };

/** What a Director tool call carries about the screen (v4 `stage`: the item in focus on the Director's stage). */
export type ToolContext = {
  /** 2026-11-03: the Director's own call (not a tap): results name tools as the agent calls them (merged tools) */
  agent?: boolean;
  projectId?: string | null;
  stage?: { kind: string; ids: Record<string, string | number>; minimised: boolean } | null;
  /** v4 (D7): report_problem only: the screen and the tab's recent Sentry event ids */
  report?: { path: string; sentryEventIds: string[] } | null;
};

export interface Api {
  me(): Promise<Me>;
  login(email: string, password: string): Promise<void>;
  acceptInvite(p: { code: string; name: string; email: string; password: string; /** v4 LG2: the Terms box was ticked */ termsVersion?: string }): Promise<void>;
  logout(): Promise<void>;

  credits(): Promise<{ balance: number; ledger: LedgerEntry[] }>;
  packs(): Promise<Packs>;
  checkout(packId: string): Promise<{ url: string }>;

  channels(): Promise<Channel[]>;
  createChannel(p: { name: string; brand?: Partial<Brand> }): Promise<Channel>;
  patchChannel(id: string, p: { name?: string; brand?: Partial<Brand> }): Promise<Channel>;

  projects(): Promise<Project[]>;
  project(id: string): Promise<Project>;
  createProject(p: NewProject): Promise<Project>;
  patchProject(id: string, p: Partial<Pick<Project, "title" | "script" | "channelId">> & { brief?: Partial<AdBrief> | VideoBriefDraft }): Promise<Project>;

  // v3 formats (spec-v3-formats.md)
  formats(): Promise<FormatInfo[]>;
  patchCharacter(id: string, charId: string, p: CharacterPatch): Promise<Project>;
  /** the character sheet: an image job (kind "character"), priced as a frame */
  makeSheet(id: string, charId: string): Promise<JobView>;
  voices(): Promise<VoiceInfo[]>;
  memory(): Promise<Memory[]>;
  addMemory(p: { kind?: MemoryKind; text: string }): Promise<Memory>;
  editMemory(id: string, text: string): Promise<Memory>;
  forget(id: string): Promise<void>;
  forgetAll(): Promise<void>;
  /** expectCredits (PRICE-FIRST): the price on the button; a dearer script is refused (400 CONFIRM_REQUIRED) */
  writeScript(id: string, instructions?: string, expectCredits?: number): Promise<Project>;
  /** PRICE-FIRST: each paid step's own price (with a scene: frame, clip, sounds too) and whether it needs a yes */
  prices(id: string, sceneId?: string | null, model?: string | null): Promise<Prices>;
  /** mode (v2): price what `produce` would do next for that scope; omitted = the whole ad as today */
  estimate(id: string, mode?: ProduceMode): Promise<Estimate>;
  /** expectCredits: the price shown on the confirm; a dearer render is refused (400 CONFIRM_REQUIRED) for a new yes */
  render(id: string, expectCredits?: number): Promise<Render>;

  // v2 shots + jobs (spec-v2-video.md W3 endpoints). Each generation returns its job right away.
  makeFrame(id: string, sceneId: string, p?: { prompt?: string; refAssetIds?: string[]; expectCredits?: number }): Promise<JobView>;
  /** v4 (V4-R1/R2): the reference pictures the scene's next frame would use, and what else could go in */
  references(id: string, sceneId: string): Promise<ReferencesView>;
  /** v4 (V4-R1/R2): save pins / removals / library pictures for the shot (free; generates nothing) */
  setReferences(id: string, sceneId: string, body: SetReferencesBody): Promise<ReferencesView>;
  /** The scene's clip's last frame as a pinned reference for another scene (default the next). Free. */
  lastFrame(id: string, sceneId: string, toSceneId?: string): Promise<{ assetId: string; fromScene: number; toScene: number; toSceneId: string }>;
  /** 2026-11-02 (the World panel): a video's places and props, and a new one (a library picture: free; else drawn, priced) */
  places(id: string): Promise<PlacesView>;
  addPlace(id: string, body: { kind: PlaceKind; name: string; description?: string; fromScene?: number; fromItem?: string; assetId?: string; expectCredits?: number }): Promise<{ job?: JobView; credits?: number; place?: PlaceView }>;
  patchPlace(id: string, kind: PlaceKind, placeId: string, patch: { name?: string; description?: string }): Promise<void>;
  setPlaceScenes(id: string, kind: PlaceKind, placeId: string, scenes: number[]): Promise<Project>;
  removePlace(id: string, kind: PlaceKind, placeId: string): Promise<void>;
  /** 2026-11-02: the picture a scene's next clip ends on (null clears it); free. models: the video models that take one */
  setClipEnd(id: string, sceneId: string, assetId: string | null): Promise<{ project: Project; modelTakesIt: boolean; modelLabel: string; models: string[] }>;
  /** 2026-11-03: a scene's fit (null = automatic); free, from the next render */
  setSceneFit(id: string, sceneId: string, fit: SceneFit | null): Promise<SceneFitInfo>;
  /** 2026-11-03: a PDF, text or Markdown file's words join the video's sources (free, undoable; the file isn't kept) */
  addDocument(id: string, file: File, signal?: AbortSignal): Promise<DocumentAdded>;
  /** 2026-11-03: a public page's words join the video's sources, its product pictures its library (free, no sign-ins) */
  readWebsite(id: string, url: string, pictures?: boolean): Promise<WebsiteRead>;
  /** v2.1: `model` is a catalog id (GET /api/models); omitted = the project's, else the account's, else the default */
  /** expectCredits: the price the user said yes to (the server refuses with CONFIRM_REQUIRED if it now costs more) */
  makeClip(id: string, sceneId: string, p?: { motionPrompt?: string; model?: string; durationS?: number; force?: boolean; expectCredits?: number }): Promise<JobView>;
  /** lip sync (2026-10-30): a scene's switch (free; the next clip's price follows it) */
  setLipSync(id: string, sceneId: string, on: boolean): Promise<void>;
  /** a scene's own picture or video model; null = back to the video's */
  setSceneModel(id: string, sceneId: string, step: "image" | "clip", model: string | null): Promise<void>;
  /** lip sync: how the scene's clip's lip sync looked (internal feedback on its model) */
  rateLipSync(id: string, sceneId: string, verdict: "good" | "bad" | "mixed"): Promise<void>;
  makeSounds(id: string, sceneId: string, expectCredits?: number): Promise<JobView>;
  makeMusic(id: string, p?: { prompt?: string; expectCredits?: number }): Promise<JobView>;
  approve(id: string, sceneId: string, what: "frame" | "clip"): Promise<void>;
  /** v4.1 B4: the script's factual lines checked against the sources */
  factcheck(id: string): Promise<{ checked: number; ok: boolean; summary: string; findings: { sceneN: number; sentence: string; status: "sourced" | "drift" | "quote" | "unsourced"; detail: string }[] } | null>;
  /** v4.1 B3: a finished version's publishing kit (raw; PublishKit.tsx normKit reads it) */
  publishKit(id: string, version: number): Promise<unknown>;
  /** 2026-11-03: a line's speed for every render ("vo" or "d<j>"; 1 = as read) */
  setLineSpeed(id: string, sceneId: string, key: string, speed: number): Promise<Project>;
  /** 2026-11-03: throw away a take waiting for review (an earlier one comes back) */
  scratch(id: string, sceneId: string, what: "frame" | "clip"): Promise<{ restored: boolean }>;
  /** "redo": rejects the take with an optional note. If the server starts the redo itself it returns that job. */
  /** expectCredits: the price shown with the redo; a dearer redo is refused (400 CONFIRM_REQUIRED) */
  reject(id: string, sceneId: string, what: "frame" | "clip", note?: string, expectCredits?: number): Promise<JobView | null>;
  approveMusic(id: string): Promise<void>;
  rejectMusic(id: string, note?: string, expectCredits?: number): Promise<JobView | null>;
  /** v2.1: nudge (atS), mute (enabled) or level (gainDb) a scene's effects; returns the updated project */
  patchSfx(id: string, sceneId: string, sfx: SfxEdit[]): Promise<Project>;
  /** the scene's clip (or still) with its sounds mixed over it: free, made at once (tweaks are heard, not saved) */
  sceneSoundPreview(id: string, sceneId: string, body?: { tweaks?: SfxEdit[]; voice?: boolean; mix?: SoundMix }): Promise<SceneSoundPreview>;
  /** 2026-10-31 (api v4/narration.ts): the voices on their own. scenes: scene ids; empty = every scene */
  voicePrice(id: string, sceneIds?: string[]): Promise<VoicePlan>;
  recordVoices(id: string, sceneIds: string[] | null, expectCredits: number): Promise<{ plan: VoicePlan; jobs: JobView[] }>;
  playVoices(id: string, sceneIds?: string[]): Promise<VoiceAudio>;
  voicePass(id: string): Promise<VoicePass>;
  setLipLead(id: string, sceneId: string, seconds: number): Promise<void>;
  /** 2026-10-31 (api v4/scenelines.ts): move / split / add / remove / reorder a scene's lines; free, undoable */
  editLines(id: string, body: LinesEdit): Promise<{ project: Project; clipsSetAside: number[] }>;

  // v2.1 models
  models(step?: ModelStep): Promise<ModelInfo[]>;
  myModels(): Promise<MyModels>;
  setMyModel(step: ModelStep, model: string): Promise<void>;
  setProjectModel(id: string, step: ModelStep, model: string): Promise<void>;

  // v2.1 versions
  versions(id: string): Promise<Version[]>;
  /** V4-DL: every file of the project, grouped, for the "Download files" sheet */
  projectFiles(id: string): Promise<ProjectFiles>;
  restoreVersion(id: string, n: number): Promise<Project>;
  /** phase 2: "Change thumbnail": the poster of a version (default: the one in use) at atS seconds; null = automatic */
  setThumbnail(id: string, p: { version?: number | null; atS: number | null }): Promise<ThumbnailResult>;
  produce(id: string, mode: ProduceMode): Promise<JobView[]>;
  jobs(id: string): Promise<JobView[]>;
  job(jobId: string): Promise<JobView>;
  getRender(id: string): Promise<Render>;
  projectRenders(id: string): Promise<Render[]>;
  /** v4 (V4-P1): the one next step for a video (core nextStep(), the same one whats_happening speaks) */
  nextStep(id: string): Promise<NextStepData | null>;

  library(q?: LibraryQuery): Promise<Asset[]>;
  /** v4 (V4-A1): start, send (progress 0..1) and finish one upload. A first upload without the terms ticked throws ApiError 428 TERMS_NEEDED. */
  uploadFile(file: File, opts: UploadOpts, onProgress?: (sent: number) => void, signal?: AbortSignal): Promise<Asset>;
  /** v4 (V4-A2): rename, retag, give a role or move an item; and run auto-tagging again (the result arrives as an "asset" event) */
  patchLibrary(id: string, patch: LibraryPatch): Promise<Asset>;
  retagLibrary(id: string): Promise<Asset>;
  /** v4 (V4-A1): storage use and whether the upload terms were accepted */
  myStorage(): Promise<StorageInfo>;
  /** v4 (V4-M1): the video's Suno prompt pack (null when there's none), write a new one (paid), or edit it (free) */
  sunoPack(id: string): Promise<SunoState | null>;
  writeSunoPack(id: string, args?: SunoWriteArgs): Promise<{ suno: SunoState; credits: number; balance: number | null }>;
  patchSunoPack(id: string, patch: SunoPatch): Promise<SunoState>;
  saveLibrary(p: SaveItem): Promise<Asset>;

  /** projectId: the ad open on screen (the Director's prompt mentions it); tz: for its greeting */
  directorSession(ctx?: { projectId?: string | null; tz?: string; text?: boolean }): Promise<DirectorSession>;
  /** context.projectId: the ad open on screen, so "write the script" needs no "which ad?"; v4: `stage`, what the
   *  Director's stage shows (spec-v4 section 8.4) */
  directorTool(name: string, args: Record<string, unknown>, context?: ToolContext): Promise<ToolResponse>;
  /** v2: save a summary of the conversation before a Director voice switch (becomes the `carryover` variable) */
  saveCarryover(summary: string): Promise<void>;
  /** v3 Key Light Director: the user's Director character (PUT returns whether the voice or name changed) */
  directorPersona(): Promise<Persona>;
  setDirectorPersona(change: PersonaChange): Promise<{ persona: Persona; voiceChanged: boolean; nameChanged: boolean }>;
  /** v2.1: the call closed after `seconds`; v3: it's charged per minute, so this returns what it cost and the balance */
  /** `error`: why the call dropped (the voice SDK's message), so a provider outage is noticed (2026-11-02) */
  directorSessionEnd(p: { sessionId: string; conversationId?: string; seconds: number; error?: string }): Promise<{ credits: number; balance: number | null }>;
  /** v3: the call connected (its ElevenLabs conversation id), so it's billed even if the end never arrives */
  directorSessionConnect(p: { sessionId: string; conversationId: string }): Promise<void>;
  /** v4 rolling hold: the once-a-minute tick of an open call (charges the time used, holds the next 5 minutes);
   *  secondsLeft is how long the call may still run (null: no answer) */
  directorSessionTick(p: { sessionId: string; seconds: number }): Promise<{ secondsLeft: number | null; balance: number | null; ended: boolean }>;

  // admin
  adminCosts(window: CostWindow): Promise<AdminCosts>;
  /** D: people who signed in with no invite or purchase yet */
  accessRequests(): Promise<AccessRequest[]>;
  approveAccess(userId: string): Promise<void>;
  invites(): Promise<Invite[]>;
  createInvite(p: { email?: string | null; credits?: number; expiresDays?: number | null }): Promise<{ code: string; url: string }>;
  revokeInvite(code: string): Promise<void>;
  // beta gate: admin -> People
  people(q?: { q?: string; access?: Person["access"] }): Promise<PeopleList>;
  person(id: string): Promise<{ person: Person; ledger: PersonLedgerEntry[] }>;
  /** lets a pending account in with `credits` (the trial default when omitted) */
  approvePerson(id: string, p: { credits?: number; note?: string | null }): Promise<Person>;
  /** gives credits; `idempotencyKey` (one per click of Give) makes a double click or a retry one grant */
  grantCredits(id: string, p: { credits: number; note?: string | null; idempotencyKey: string }): Promise<{ person: Person; replayed: boolean }>;
  suspendPerson(id: string): Promise<Person>;
  restorePerson(id: string): Promise<Person>;

  /** Subscribe to GET /api/events. Returns an unsubscribe function. */
  events(on: (e: StudioEvent) => void, onState?: (open: boolean) => void): () => void;
}

// ------------------------------------------------------------------ normalisers (the spec leaves some wire shapes open)

type Json = Record<string, unknown>;
const isObj = (v: unknown): v is Json => typeof v === "object" && v !== null && !Array.isArray(v);
const str = (v: unknown, d = "") => (typeof v === "string" ? v : v == null ? d : String(v));
const num = (v: unknown, d = 0) => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && v.trim() !== "" && Number.isFinite(+v) ? +v : d);

/** `[...]`, `{items: [...]}`, `{projects: [...]}`... */
function list(v: unknown, ...keys: string[]): unknown[] {
  if (Array.isArray(v)) return v;
  if (isObj(v)) for (const k of [...keys, "items", "data", "results"]) if (Array.isArray(v[k])) return v[k] as unknown[];
  return [];
}
/** `{...}` or `{project: {...}}` */
function one(v: unknown, key: string): Json {
  if (isObj(v) && isObj(v[key])) return v[key] as Json;
  return isObj(v) ? v : {};
}
const pick = (o: Json, ...keys: string[]) => {
  for (const k of keys) if (o[k] !== undefined) return o[k];
  return undefined;
};

export function normUser(v: unknown): User {
  const o = one(v, "user");
  const voiceId = pick(o, "directorVoiceId", "director_voice_id");
  const voiceName = pick(o, "directorVoiceName", "director_voice_name");
  return {
    id: str(o.id),
    email: str(o.email),
    name: str(o.name),
    role: o.role === "admin" ? "admin" : "user",
    directorVoiceId: voiceId ? str(voiceId) : null,
    directorVoiceName: voiceName ? str(voiceName) : null,
    emailVideoReady: o.emailVideoReady !== false,
    director: normPersona(o.director, { directorVoiceId: voiceId ? str(voiceId) : null, directorVoiceName: voiceName ? str(voiceName) : null }),
    // v2.1 (D): a Clerk sign-up waits as "pending" until someone lets it in; beta gate: "suspended" is kept out too
    access: o.access === "pending" || o.access === "suspended" ? o.access : "active",
    accessRequestedAt: o.accessRequestedAt ? str(o.accessRequestedAt) : null,
    // v4 LG2: an api that predates these says nothing, which means "no country page, no Terms checkbox"
    geoBlocked: o.geoBlocked === true,
    termsRequired: o.termsRequired === true,
    currentTermsVersion: o.currentTermsVersion ? str(o.currentTermsVersion) : null,
    termsVersion: o.termsVersion ? str(o.termsVersion) : null,
  };
}

const ERROR_CODES: JobErrorCode[] = ["moderation", "provider", "timeout", "cap"];
function normSuggestion(v: unknown): JobView["suggestion"] {
  if (!isObj(v) || !v.model) return null;
  return { model: str(v.model), label: str(v.label, str(v.model)), credits: num(v.credits) };
}

const STEPS: ModelStep[] = ["image", "clip", "sfx", "music", "voice"];
function stepMap(v: unknown): Partial<Record<ModelStep, string>> {
  const out: Partial<Record<ModelStep, string>> = {};
  if (isObj(v)) for (const s of STEPS) if (typeof v[s] === "string" && v[s]) out[s] = v[s] as string;
  return out;
}

export function normModel(v: unknown): ModelInfo {
  const o = isObj(v) ? v : {};
  const strs = (x: unknown) => (Array.isArray(x) ? x.map((y) => str(y)) : []);
  const per5 = pick(o, "creditsPer5s", "credits_per_5s");
  return {
    id: str(o.id),
    step: STEPS.includes(o.step as ModelStep) ? (o.step as ModelStep) : "clip",
    label: str(o.label, str(o.id)),
    creditsPer5s: per5 === undefined ? undefined : num(per5),
    credits: o.credits === undefined ? undefined : num(o.credits),
    durations: Array.isArray(o.durations) ? o.durations.map((d) => num(d)) : undefined,
    aspects: Array.isArray(o.aspects) ? strs(o.aspects) : undefined,
    strengths: strs(o.strengths),
    constraints: strs(o.constraints),
    rateSource: pick(o, "rateSource", "rate_source") === "unverified" ? "unverified" : "documented",
    isDefault: pick(o, "isDefault", "is_default") === true,
    unit: o.unit ? str(o.unit) : undefined,
  };
}

export function normVersion(v: unknown): Version {
  const o = isObj(v) ? v : {};
  return {
    version: num(o.version),
    renderId: str(pick(o, "renderId", "render_id")),
    createdAt: str(pick(o, "createdAt", "created_at")),
    durationS: num(pick(o, "durationS", "duration_s")),
    url: str(o.url),
    thumbUrl: (pick(o, "thumbUrl", "thumb_url") as string) ?? undefined,
    previewUrl: (pick(o, "previewUrl", "preview_url") as string) ?? undefined,
    posterAtS: typeof o.posterAtS === "number" ? o.posterAtS : null,
    posterCustom: o.posterCustom === true,
    isLatest: pick(o, "isLatest", "is_latest") === true,
    downloadUrl: (pick(o, "downloadUrl", "download_url") as string) ?? null,
    fileName: (pick(o, "fileName", "file_name") as string) ?? null,
    thumbDownloadUrl: (pick(o, "thumbDownloadUrl", "thumb_download_url") as string) ?? null,
  };
}

const COST_KINDS: CostKind[] = ["frame", "clip", "voice", "music", "sfx", "cues", "render", "script", "director", "other"];
const markupOf = (v: unknown) => (v === null || v === undefined || !Number.isFinite(Number(v)) ? null : num(v));
export function normCosts(v: unknown): AdminCosts {
  const o = isObj(v) ? v : {};
  return {
    window: str(o.window, "7d"),
    spendUsd: num(o.spendUsd),
    chargedUsd: num(o.chargedUsd),
    marginUsd: num(o.marginUsd),
    markup: markupOf(o.markup),
    byKind: list(o.byKind).map((r) => {
      const x = isObj(r) ? r : {};
      return { kind: COST_KINDS.includes(x.kind as CostKind) ? (x.kind as CostKind) : "other", spendUsd: num(x.spendUsd), chargedUsd: num(x.chargedUsd), markup: markupOf(x.markup), count: num(x.count), unverified: x.unverified === true };
    }),
    topUsers: list(o.topUsers).map((r) => {
      const x = isObj(r) ? r : {};
      return { userId: str(x.userId), email: str(x.email), spendUsd: num(x.spendUsd), chargedUsd: num(x.chargedUsd) };
    }),
    providerOutOfCredit: isObj(o.providerOutOfCredit)
      ? {
          provider: str(o.providerOutOfCredit.provider),
          at: str(o.providerOutOfCredit.at),
          remaining: typeof o.providerOutOfCredit.remaining === "number" ? o.providerOutOfCredit.remaining : null,
          required: typeof o.providerOutOfCredit.required === "number" ? o.providerOutOfCredit.required : null,
        }
      : null,
  };
}

const JOB_KINDS: JobKind[] = ["frame", "clip", "cues", "sfx", "music", "render", "character"];

// ------------------------------------------------------------------ v3: formats, characters, memory, voices

const strs = (x: unknown) => (Array.isArray(x) ? x.map((y) => str(y)) : []);
export function normFormat(v: unknown): FormatInfo | null {
  const o = isObj(v) ? v : {};
  if (!isFormatId(o.id)) return null;
  const sample = isObj(o.sample) && (o.sample.videoUrl || o.sample.posterUrl) ? { title: str(o.sample.title), posterUrl: str(o.sample.posterUrl), videoUrl: str(o.sample.videoUrl), ...(o.sample.previewUrl ? { previewUrl: str(o.sample.previewUrl) } : {}), aspect: str(o.sample.aspect, "16:9"), durationS: num(o.sample.durationS) } : null;
  return {
    ...(o as unknown as FormatInfo),
    id: o.id,
    name: str(o.name, o.id),
    group: (FORMAT_GROUPS as readonly string[]).includes(str(o.group)) ? (o.group as FormatInfo["group"]) : "Stories",
    tagline: str(o.tagline),
    description: str(o.description),
    examples: strs(o.examples),
    status: o.status === "beta" ? "beta" : "ready",
    durations: Array.isArray(o.durations) ? o.durations.map((d) => num(d)).filter((d) => d > 0) : [60],
    aspects: (Array.isArray(o.aspects) ? o.aspects : ["16:9"]).filter((a): a is FormatInfo["aspects"][number] => a === "9:16" || a === "16:9" || a === "1:1"),
    looks: list(o.looks).map((l) => {
      const x = isObj(l) ? l : {};
      return { id: str(x.id), name: str(x.name, str(x.id)), description: str(x.description), render: str(x.render), imageStyle: x.imageStyle ? str(x.imageStyle) : undefined };
    }),
    roles: strs(o.roles),
    briefFields: list(o.briefFields).filter(isObj) as unknown as FormatInfo["briefFields"],
    voices: (["narrator", "cast", "hosts", "none"] as const).find((x) => x === o.voices) ?? "narrator",
    motion: (["stills", "clips", "mixed"] as const).find((x) => x === o.motion) ?? "stills",
    sample,
  };
}

export function normCharacter(v: unknown): Character {
  const o = isObj(v) ? v : {};
  const ref = isObj(o.ref) ? o.ref : null;
  return {
    id: str(o.id),
    name: str(o.name, "Unnamed"),
    look: str(o.look),
    voice: str(o.voice),
    voiceId: o.voiceId ? str(o.voiceId) : undefined,
    refAssetId: o.refAssetId ? str(o.refAssetId) : undefined,
    refUrl: (pick(o, "refUrl", "sheetUrl", "ref_url") as string) ?? (ref ? str(ref.url) : null) ?? (isObj(o.sheet) && o.sheet.url ? str(o.sheet.url) : null) ?? null,
    voiceName: (pick(o, "voiceName", "voice_name") as string) ?? null,
  };
}

const MEMORY_KINDS: MemoryKind[] = ["goal", "audience", "style", "format", "voice", "brand", "preference", "note"];
export function normMemory(v: unknown): Memory {
  const o = one(v, "memory");
  return {
    id: str(o.id),
    kind: MEMORY_KINDS.includes(o.kind as MemoryKind) ? (o.kind as MemoryKind) : "note",
    text: str(o.text),
    source: o.source === "user" ? "user" : "director",
    createdAt: str(pick(o, "createdAt", "created_at")) || undefined,
  };
}

export function normVoice(v: unknown): VoiceInfo {
  const o = isObj(v) ? v : {};
  return { voiceId: str(pick(o, "voiceId", "voice_id", "id")), name: str(o.name), description: str(o.description), tags: strs(o.tags), gender: o.gender ? str(o.gender) : undefined, age: o.age ? str(o.age) : undefined, accent: o.accent ? str(o.accent) : undefined };
}
const JOB_STATUSES: JobStatus[] = ["queued", "running", "done", "failed"];
export function normJob(v: unknown): JobView {
  const o = one(v, "job");
  let progress = num(o.progress);
  if (progress > 1) progress /= 100;
  const status = JOB_STATUSES.includes(o.status as JobStatus) ? (o.status as JobStatus) : o.status === "succeeded" || o.status === "completed" ? "done" : o.status === "error" ? "failed" : "queued";
  const now = new Date().toISOString();
  return {
    id: str(pick(o, "id", "jobId", "job_id")),
    projectId: (pick(o, "projectId", "project_id") as string) || null,
    characterId: (pick(o, "characterId", "character_id") as string) || null,
    sceneId: (pick(o, "sceneId", "scene_id") as string) ?? null,
    kind: JOB_KINDS.includes(o.kind as JobKind) ? (o.kind as JobKind) : "frame",
    status,
    step: (o.step as string) ?? null,
    progress: Math.max(0, Math.min(1, status === "done" ? 1 : progress)),
    credits: num(pick(o, "credits", "creditsReserved", "credits_reserved")),
    assetId: (pick(o, "assetId", "asset_id", "resultAssetId", "result_asset_id") as string) ?? null,
    url: (o.url as string) ?? null,
    error: (o.error as string) ?? null,
    errorCode: ERROR_CODES.includes(pick(o, "errorCode", "error_code") as JobErrorCode) ? (pick(o, "errorCode", "error_code") as JobErrorCode) : null,
    suggestion: normSuggestion(o.suggestion),
    ...(o.stopped === true ? { stopped: true } : {}),
    ...(pick(o, "compareId", "compare_id") ? { compareId: str(pick(o, "compareId", "compare_id")) } : {}), // v4 (V4-MC)
    ...(isObj(o.lipSync) ? { lipSync: { mode: str(o.lipSync.mode), ask: o.lipSync.ask === true } } : {}), // lip sync
    createdAt: str(pick(o, "createdAt", "created_at"), now),
    updatedAt: str(pick(o, "updatedAt", "updated_at"), now),
  };
}

/** V4-DL: a view's same-origin download link (null when the API has none). */
const dlOf = (o: Record<string, unknown>) => (pick(o, "downloadUrl", "download_url") as string) || null;

function normShots(v: unknown): Record<string, Shot> {
  const out: Record<string, Shot> = {};
  if (!isObj(v)) return out;
  for (const [sceneId, raw] of Object.entries(v)) {
    const s = isObj(raw) ? raw : {};
    const f = isObj(s.frame) ? s.frame : null;
    const c = isObj(s.clip) ? s.clip : null;
    const amb = isObj(s.ambience) ? s.ambience : null;
    out[sceneId] = {
      refAssetIds: Array.isArray(s.refAssetIds) ? s.refAssetIds.map((x) => str(x)) : [],
      clipModel: (pick(s, "clipModel", "clip_model") as string) || null,
      imageModel: (pick(s, "imageModel", "image_model") as string) || null,
      ...(isObj(s.defaults) ? { defaults: { image: str(s.defaults.image), clip: str(s.defaults.clip), ...(s.defaults.clipWhy === "rule" || s.defaults.clipWhy === "lipsync" ? { clipWhy: s.defaults.clipWhy } : {}) } } : {}),
      ...(typeof s.lipSync === "boolean" ? { lipSync: s.lipSync } : {}), // lip sync: the scene's own switch
      ...(typeof s.lipLeadS === "number" && s.lipLeadS > 0 ? { lipLeadS: s.lipLeadS } : {}), // its lead-in (2026-10-31)
      frame: f ? { assetId: str(f.assetId), url: str(f.url), status: f.status === "approved" ? "approved" : "draft", prompt: str(f.prompt), downloadUrl: dlOf(f), ...(typeof f.model === "string" && f.model ? { model: f.model } : isObj(f.made) && typeof f.made.imageModel === "string" ? { model: f.made.imageModel } : {}), ...(Array.isArray(f.refs) ? { refs: f.refs.filter(isObj).map(normRefUse) } : {}) } : undefined,
      clip: c
        ? { assetId: str(c.assetId), url: str(c.url), status: c.status === "approved" ? "approved" : "draft", model: str(c.model), durationS: num(c.durationS), motionPrompt: str(c.motionPrompt), shotType: (pick(c, "shotType", "shot_type") as string) ?? null, downloadUrl: dlOf(c) }
        : undefined,
      sfx: list(s.sfx).map((x) => {
        const e = isObj(x) ? x : {};
        const atS = num(e.atS);
        return { assetId: str(e.assetId), url: str(e.url), atS, label: str(e.label), enabled: e.enabled !== false, gainDb: num(e.gainDb), detectedAtS: num(pick(e, "detectedAtS", "detected_at_s"), atS), strength: (e.strength as string) ?? null, downloadUrl: dlOf(e) };
      }),
      ambience: amb ? { assetId: str(amb.assetId), url: str(amb.url), downloadUrl: dlOf(amb) } : undefined,
      ...(Array.isArray(s.comparisons) && s.comparisons.length ? { comparisons: s.comparisons.map(normComparison) } : {}), // v4 (V4-MC)
      ...(isObj(s.clipEnd) && typeof s.clipEnd.assetId === "string" ? { clipEnd: { assetId: s.clipEnd.assetId, name: str(s.clipEnd.name), url: str(s.clipEnd.url) || null, thumbUrl: str(s.clipEnd.thumbUrl) || null } } : {}), // 2026-11-02
      ...(s.fit === "video" || s.fit === "audio" ? { fit: s.fit as "video" | "audio" } : {}), // 2026-11-03
      ...(isObj(s.lineSpeeds) ? { lineSpeeds: Object.fromEntries(Object.entries(s.lineSpeeds).filter(([k, v]) => /^(vo|d\d{1,2})$/.test(k) && typeof v === "number" && v >= 0.5 && v <= 2)) as Record<string, number> } : {}), // 2026-11-03
    };
  }
  return out;
}

function normMusic(v: unknown): MusicBed | null {
  if (!isObj(v)) return null;
  return { assetId: str(v.assetId), url: str(v.url), status: v.status === "approved" ? "approved" : "draft", prompt: str(v.prompt), downloadUrl: dlOf(v) };
}

function normGrant(v: unknown): PersonGrant {
  const o = isObj(v) ? v : {};
  const by = isObj(o.by) ? o.by : null;
  return { id: str(o.id), credits: num(o.credits), note: (o.note as string) ?? null, kind: o.kind === "approval" ? "approval" : "grant", by: by ? { email: str(by.email), name: (by.name as string) ?? null } : null, createdAt: str(o.createdAt) };
}

export function normPerson(v: unknown): Person {
  const o = isObj(v) ? v : {};
  const t = (k: string) => (o[k] ? str(o[k]) : null);
  return {
    id: str(o.id),
    email: str(o.email),
    name: str(o.name),
    role: o.role === "admin" ? "admin" : "user",
    access: o.access === "pending" || o.access === "suspended" ? o.access : "active",
    balance: num(o.balance),
    signedUpAt: str(o.signedUpAt),
    lastActiveAt: t("lastActiveAt"),
    requestedAt: t("requestedAt"),
    note: t("note"),
    approvedAt: t("approvedAt"),
    suspendedAt: t("suspendedAt"),
    recentGrants: list(o.recentGrants).map(normGrant),
  };
}

export function normInvite(v: unknown): Invite {
  const o = one(v, "invite");
  const usedBy = pick(o, "usedBy", "used_by", "usedByEmail", "used_by_email");
  const revoked = pick(o, "revokedAt", "revoked_at", "revoked");
  const expiresAt = (pick(o, "expiresAt", "expires_at") as string) ?? null;
  const given = str(o.status);
  const status: Invite["status"] =
    given === "open" || given === "used" || given === "revoked" || given === "expired"
      ? given
      : revoked
        ? "revoked"
        : usedBy
          ? "used"
          : expiresAt && new Date(expiresAt).getTime() < Date.now()
            ? "expired"
            : "open";
  return {
    code: str(o.code),
    url: str(o.url) || (typeof location !== "undefined" ? `${location.origin}/invite/${encodeURIComponent(str(o.code))}` : ""),
    email: (o.email as string) ?? null,
    credits: num(o.credits),
    status,
    usedBy: usedBy ? str(usedBy) : null,
    createdAt: str(pick(o, "createdAt", "created_at")) || undefined,
    expiresAt,
  };
}

export function normBrand(v: unknown): Brand {
  const o = isObj(v) ? v : {};
  const c = isObj(o.colors) ? o.colors : {};
  const narrator = isObj(o.narrator) ? { voiceId: str(o.narrator.voiceId ?? o.narrator.voice_id), name: str(o.narrator.name) } : null;
  return {
    colors: { bg: str(c.bg, "#101522"), fg: str(c.fg, "#F4EFE6"), accent: str(c.accent, "#FFB23E") },
    logoAssetId: (pick(o, "logoAssetId", "logo_asset_id") as string) ?? null,
    logoUrl: (pick(o, "logoUrl", "logoSrc", "logo_url") as string) ?? null,
    font: (o.font as string) ?? null,
    tagline: (o.tagline as string) ?? null,
    cta: (o.cta as string) ?? null,
    ctaUrl: (pick(o, "ctaUrl", "cta_url") as string) ?? null,
    narrator,
    // v4 (V4-H2): intro/outro, fonts, publish targets (absent stays absent, so a PATCH never invents them)
    ...(o.introAssetId !== undefined ? { introAssetId: (o.introAssetId as string) ?? null } : {}),
    ...(o.outroAssetId !== undefined ? { outroAssetId: (o.outroAssetId as string) ?? null } : {}),
    ...(isObj(o.fonts) ? { fonts: { ...(typeof o.fonts.heading === "string" ? { heading: o.fonts.heading } : {}), ...(typeof o.fonts.body === "string" ? { body: o.fonts.body } : {}) } } : {}),
    ...(Array.isArray(o.publishTo) ? { publishTo: (o.publishTo as unknown[]).filter(isObj).map((x) => ({ platform: str(x.platform) as NonNullable<Brand["publishTo"]>[number]["platform"], ...(typeof x.handle === "string" && x.handle ? { handle: x.handle } : {}) })) } : {}),
  };
}

export function normChannel(v: unknown): Channel {
  const o = one(v, "channel");
  return { id: str(o.id), name: str(o.name), brand: normBrand(o.brand) };
}

export function normProject(v: unknown): Project {
  const o = one(v, "project");
  // scene pictures arrive as scene.image (a library asset view with a signed url) or flat imageUrl
  let script: Project["script"] = null;
  if (isObj(o.script) && Array.isArray(o.script.scenes)) {
    const scenes = (o.script.scenes as unknown[]).map((x) => {
      const sc = isObj(x) ? x : {};
      const img = isObj(sc.image) ? sc.image : null;
      return { ...sc, imageUrl: (sc.imageUrl as string) ?? (img ? str(img.url) || null : null), imageAssetId: (sc.imageAssetId as string) ?? (img ? str(img.id) : null) };
    });
    script = { ...(o.script as unknown as NonNullable<Project["script"]>), scenes: scenes as unknown as NonNullable<Project["script"]>["scenes"] };
  }
  const format = isFormatId(o.format) ? o.format : "ad";
  // v3: the cast lives on the brief (the script's characters are merged into it by the api)
  let brief = isObj(o.brief) ? (o.brief as Partial<AdBrief>) : {};
  if (format !== "ad" && Array.isArray((brief as Json).characters)) brief = { ...brief, characters: ((brief as Json).characters as unknown[]).map(normCharacter) } as Partial<AdBrief>;
  return {
    id: str(o.id),
    format,
    channelId: (pick(o, "channelId", "channel_id") as string) ?? null,
    title: str(o.title, format === "ad" ? "Untitled ad" : "Untitled video"),
    category: str(o.category, "ads"),
    template: str(o.template, "classic-ad"),
    brief,
    script,
    status: str(o.status, "draft"),
    createdAt: str(pick(o, "createdAt", "created_at")) || undefined,
    updatedAt: str(pick(o, "updatedAt", "updated_at")) || undefined,
    thumbUrl: (pick(o, "thumbUrl", "thumb_url", "posterUrl") as string) ?? script?.scenes.find((s) => s.imageUrl)?.imageUrl ?? null,
    previewUrl: (pick(o, "previewUrl", "preview_url") as string) ?? null,
    latestRender: isObj(o.latestRender) ? normRender(o.latestRender) : null,
    shots: normShots(o.shots),
    music: normMusic(o.music),
    suno: normSuno(o.suno),
    modelPrefs: stepMap(pick(o, "modelPrefs", "model_prefs")),
    models: stepMap(o.models),
    needsReview: o.needsReview === undefined ? undefined : num(o.needsReview),
    jobs: list(o.jobs).map(normJob),
  };
}

const STATUSES: RenderStatus[] = ["queued", "running", "done", "failed"];
export function normRender(v: unknown): Render {
  const o = one(v, "render");
  let progress = num(o.progress);
  if (progress > 1) progress /= 100;
  const status = STATUSES.includes(o.status as RenderStatus) ? (o.status as RenderStatus) : "queued";
  return {
    id: str(pick(o, "id", "renderId", "render_id")),
    projectId: str(pick(o, "projectId", "project_id")),
    status,
    step: (o.step as string) ?? null,
    progress: Math.max(0, Math.min(1, status === "done" ? 1 : progress)),
    error: (o.error as string) ?? null,
    videoAssetId: (pick(o, "videoAssetId", "video_asset_id") as string) ?? null,
    videoUrl: (pick(o, "videoUrl", "video_url") as string) ?? (isObj(o.video) ? str(o.video.url) || null : null),
    posterUrl: (pick(o, "posterUrl", "poster_url") as string) ?? null,
    previewUrl: (pick(o, "previewUrl", "preview_url") as string) ?? null,
    credits: num(o.credits),
    createdAt: str(pick(o, "createdAt", "created_at")) || undefined,
  };
}

export function normEstimate(v: unknown): Estimate {
  const o = one(v, "estimate");
  const breakdown: EstimateLine[] = list(o.breakdown).map((b) => {
    const x = isObj(b) ? b : {};
    const qty = pick(x, "qty", "quantity");
    const detail = pick(x, "detail") ?? (qty !== undefined ? `${str(qty)}${x.unit ? ` ${str(x.unit)}` : ""}` : undefined);
    return { label: str(pick(x, "label", "item", "name", "action")), credits: num(x.credits), detail: detail === undefined ? undefined : str(detail) };
  });
  return { credits: num(o.credits), usd: num(o.usd), breakdown };
}

/**
 * fix-produce: the price of a produce mode, from POST /estimate. The confirm sheet must show the plan produce will
 * start (`produce`: music, frames, clips, sounds and the render), never the render's own charge (`estimate`), or its
 * yes (expectCredits) is short and produce refuses with CONFIRM_REQUIRED. No plan: `produceError` says why (thrown).
 * Also reads a bare plan view (CONFIRM_REQUIRED's details.plan).
 */
export function normProducePrice(v: unknown): Estimate {
  const o = isObj(v) ? v : {};
  const plan = isObj(o.produce) ? o.produce : "steps" in o && "credits" in o ? o : null;
  if (plan) {
    const e = normEstimate({ estimate: plan });
    const clips = pick(plan, "clips");
    return { ...e, usd: e.usd || 0, ...(clips !== undefined ? { clips: num(clips) } : {}) };
  }
  if (isObj(o.produceError)) {
    const pe = o.produceError;
    throw new ApiError(str(pe.message) || "There's nothing to make for that right now.", 400, pe.code ? str(pe.code) : null, pe.details ?? null);
  }
  return normEstimate(v);
}

export function normAsset(v: unknown): Asset {
  const o = isObj(v) && isObj(v.item) ? v.item : one(v, "asset");
  return {
    id: str(pick(o, "id", "assetId", "asset_id")),
    kind: str(o.kind, "image") as AssetKind,
    name: str(o.name, "Untitled"),
    tags: Array.isArray(o.tags) ? o.tags.map((t) => str(t)) : [],
    meta: isObj(o.meta) ? o.meta : {},
    url: (pick(o, "url", "src", "mediaUrl") as string) ?? null,
    thumbUrl: (pick(o, "thumbUrl", "thumb_url") as string) ?? null,
    previewUrl: (pick(o, "previewUrl", "preview_url") as string) ?? null,
    downloadUrl: (pick(o, "downloadUrl", "download_url") as string) ?? null,
    projectId: (pick(o, "projectId", "project_id") as string) ?? null,
    channelId: (pick(o, "channelId", "channel_id") as string) ?? null,
    createdAt: str(pick(o, "createdAt", "created_at")) || undefined,
    // v4 (V4-A1): role, origin, scope, collection and the auto tags
    role: (pick(o, "role") as string) ?? null,
    origin: (pick(o, "origin") as string) ?? undefined,
    scope: (pick(o, "scope") as string) ?? undefined,
    collectionId: (pick(o, "collectionId", "collection_id") as string) ?? null,
    autoTags: (() => {
      const t = pick(o, "autoTags", "auto_tags");
      return Array.isArray(t) ? t.map((x) => str(x)) : [];
    })(),
    mime: (pick(o, "mime") as string) ?? null,
    bytes: (() => {
      const b = pick(o, "bytes");
      return b == null ? null : num(b);
    })(),
  };
}

export function normPack(v: unknown): CreditPack {
  const o = isObj(v) ? v : {};
  const cents = pick(o, "usdCents", "priceCents", "price_cents", "amountCents", "unit_amount");
  const usd = cents !== undefined ? num(cents) / 100 : num(pick(o, "usd", "priceUsd", "price_usd", "price"));
  return { id: str(o.id), name: str(o.name ?? o.label, `${num(o.credits)} credits`), credits: num(o.credits), usd, note: (pick(o, "note", "blurb", "description", "badge") as string) ?? undefined };
}

export function normLedger(v: unknown): LedgerEntry {
  const o = isObj(v) ? v : {};
  return { id: str(o.id), delta: num(o.delta), reason: str(o.reason), ref: (o.ref as string) ?? null, createdAt: str(pick(o, "createdAt", "created_at")) || undefined };
}

/** SSE payloads: either named events (`event: render`) or `{type, ...}` messages; both end up as StudioEvent. */
export function normEvent(name: string, data: unknown): StudioEvent | null {
  const o = isObj(data) ? data : {};
  const type = str(o.type || name);
  switch (type) {
    case "render":
    case "render_progress":
      return { type: "render", render: normRender(isObj(o.render) ? o.render : o) };
    case "job":
      // v2: {type:"job", job: JobView}. (v1 never sent "job"; a bare render-shaped payload still reads as a job.)
      return { type: "job", job: normJob(isObj(o.job) ? o.job : o) };
    case "credits":
    case "balance":
      return { type: "credits", balance: num(pick(o, "balance", "credits")) };
    case "asset": {
      const asset = normAsset(isObj(o.asset) ? o.asset : o);
      const sceneId = str(o.sceneId ?? asset.meta.sceneId ?? asset.meta.scene_id);
      if (asset.kind === "image" && sceneId && asset.projectId && asset.url)
        return { type: "scene_image", projectId: asset.projectId, sceneId, url: asset.url, assetId: asset.id };
      return { type: "asset", asset };
    }
    case "scene_image":
      return { type: "scene_image", projectId: str(pick(o, "projectId", "project_id")), sceneId: str(pick(o, "sceneId", "scene_id")), url: str(o.url), assetId: str(pick(o, "assetId", "asset_id")) || undefined };
    case "project":
      return { type: "project", projectId: str(pick(o, "projectId", "project_id", "id")) };
    case "change": // v4 (V4-U1)
      return { type: "change", projectId: str(o.projectId) || null, change: o.change };
    case "preview": {
      // v4 (T4): T3's range preview progress
      const preview = normPreviewEvent(o);
      return preview ? { type: "preview", preview } : null;
    }
    default:
      return null;
  }
}

// ------------------------------------------------------------------ the real client

// v2.1 (D): signed in with Clerk, every call carries its session token (src/auth sets the source; null = cookie only)
let authToken: (() => Promise<string | null>) | null = null;
export function setAuthTokenSource(f: (() => Promise<string | null>) | null) {
  authToken = f;
}

/** 2026-11-02: GETs a file the api serves behind sign-in (an admin's view of a report's screenshot) as a blob URL. */
export async function authedBlobUrl(path: string): Promise<string> {
  const token = authToken ? await authToken().catch(() => null) : null;
  const r = await fetch(`/api/${path.replace(/^\/+(api\/+)?/, "")}`, { credentials: "include", headers: token ? { authorization: `Bearer ${token}` } : {} });
  if (!r.ok) throw new ApiError(`GET /api/${path} failed (${r.status})`, r.status, null, null);
  return URL.createObjectURL(await r.blob());
}

export async function call<T = unknown>(method: string, path: string, body?: unknown, opts: { keepalive?: boolean } = {}): Promise<T> {
  const token = authToken ? await authToken().catch(() => null) : null;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["content-type"] = "application/json";
  if (token) headers.authorization = `Bearer ${token}`;
  // `path` is relative to /api/; a stray leading "/" or "/api/" would make "/api//api/..." (a 404), so drop it
  const r = await fetch(`/api/${path.replace(/^\/+(api\/+)?/, "")}`, {
    method,
    credentials: "include",
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    // keepalive: the request survives the page closing (the call's minutes report sent on pagehide)
    ...(opts.keepalive ? { keepalive: true } : {}),
  });
  const text = await r.text();
  let data: unknown = undefined;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    data = text;
  }
  if (!r.ok) {
    const msg = isObj(data) ? str(data.error ?? data.message) : "";
    throw new ApiError(msg || `${method} /api/${path} failed (${r.status})`, r.status, isObj(data) && data.code ? str(data.code) : null, isObj(data) ? (data.details ?? null) : null);
  }
  return data as T;
}
/** v4 uploads: PUT the file's bytes with progress (XHR: fetch has no upload progress). `ours` = our api (cookie + token). */
/** An upload the user cancelled (ApiError status 0, code CANCELLED). */
const cancelled = () => new ApiError("Cancelled.", 0, "CANCELLED");
async function putBytes(url: string, file: Blob, headers: Record<string, string>, ours: boolean, onProgress?: (sent: number) => void, signal?: AbortSignal): Promise<void> {
  const token = ours && authToken ? await authToken().catch(() => null) : null;
  await new Promise<void>((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open("PUT", url);
    if (ours) x.withCredentials = true;
    for (const [k, v] of Object.entries(headers)) if (k.toLowerCase() !== "content-length") x.setRequestHeader(k, v);
    if (token) x.setRequestHeader("authorization", `Bearer ${token}`);
    x.upload.onprogress = (e) => e.lengthComputable && onProgress?.(Math.min(0.99, e.loaded / e.total));
    x.onload = () => {
      if (x.status >= 200 && x.status < 300) return resolve();
      let msg = "";
      let code: string | null = null;
      let details: unknown = null;
      try {
        const j = JSON.parse(x.responseText) as Json;
        msg = str(j.error ?? j.message);
        code = j.code ? str(j.code) : null;
        details = j.details ?? null;
      } catch {
        /* the bucket answers in XML */
      }
      reject(new ApiError(msg || `The upload didn't go through (${x.status}).`, x.status, code, details));
    };
    x.onerror = () => reject(new ApiError("The upload didn't go through. Check your connection and try again.", 0));
    x.onabort = () => reject(cancelled());
    if (signal) {
      if (signal.aborted) return reject(cancelled());
      signal.addEventListener("abort", () => x.abort(), { once: true });
    }
    x.send(file);
  });
}
const shot = (id: string, sceneId: string) => `projects/${encodeURIComponent(id)}/shots/${encodeURIComponent(sceneId)}`;
const qs = (o: Record<string, string | undefined>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v) p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : "";
};

export const httpApi: Api = {
  async me() {
    const o = one(await call("GET", "me"), "me");
    return { user: normUser(o.user), balance: num(o.balance), channels: list(o.channels).map(normChannel) };
  },
  async login(email, password) {
    await call("POST", "auth/login", { email, password });
  },
  async acceptInvite(p) {
    await call("POST", "auth/accept-invite", p);
  },
  async logout() {
    await call("POST", "auth/logout");
  },

  async credits() {
    const o = one(await call("GET", "credits"), "credits");
    return { balance: num(o.balance), ledger: list(o.ledger).map(normLedger) };
  },
  async packs() {
    const r = await call("GET", "credits/packs");
    return { packs: list(r, "packs").map(normPack), enabled: !(isObj(r) && r.enabled === false), pendingCanBuy: isObj(r) && r.pendingCanBuy === true };
  },
  async checkout(packId) {
    const o = one(await call("POST", "credits/checkout", { packId }), "checkout");
    return { url: str(o.url) };
  },

  async channels() {
    return list(await call("GET", "channels"), "channels").map(normChannel);
  },
  async createChannel(p) {
    return normChannel(await call("POST", "channels", p));
  },
  async patchChannel(id, p) {
    return normChannel(await call("PATCH", `channels/${encodeURIComponent(id)}`, p));
  },

  async projects() {
    return list(await call("GET", "projects"), "projects").map(normProject);
  },
  async project(id) {
    return normProject(await call("GET", `projects/${encodeURIComponent(id)}`));
  },
  async createProject(p) {
    // an ad keeps v2's category/template; every other format is named by `format` alone
    const body = !p.format || p.format === "ad" ? { category: "ads", template: "classic-ad", ...p } : p;
    return normProject(await call("POST", "projects", body));
  },
  async patchProject(id, p) {
    return normProject(await call("PATCH", `projects/${encodeURIComponent(id)}`, p));
  },

  async formats() {
    return list(await call("GET", "formats"), "formats")
      .map(normFormat)
      .filter((f): f is FormatInfo => !!f);
  },
  async patchCharacter(id, charId, p) {
    const r = await call("PATCH", `projects/${encodeURIComponent(id)}/characters/${encodeURIComponent(charId)}`, p);
    // ASSUMED: returns the project; a bare character falls back to re-reading it
    return isObj(r) && (isObj(r.project) || "brief" in r) ? normProject(r) : httpApi.project(id);
  },
  async makeSheet(id, charId) {
    return normJob(await call("POST", `projects/${encodeURIComponent(id)}/characters/${encodeURIComponent(charId)}/sheet`, {}));
  },
  async voices() {
    return list(await call("GET", "voices"), "voices").map(normVoice);
  },
  async memory() {
    return list(await call("GET", "memory"), "memories", "memory").map(normMemory);
  },
  async addMemory(p) {
    return normMemory(await call("POST", "memory", p));
  },
  async editMemory(id, text) {
    return normMemory(await call("PATCH", `memory/${encodeURIComponent(id)}`, { text }));
  },
  async forget(id) {
    await call("DELETE", `memory/${encodeURIComponent(id)}`);
  },
  async forgetAll() {
    await call("DELETE", "memory");
  },
  async writeScript(id, instructions, expectCredits) {
    const r = await call("POST", `projects/${encodeURIComponent(id)}/script`, { ...(instructions ? { instructions } : {}), ...(expectCredits !== undefined ? { expectCredits } : {}) });
    // ASSUMED: returns the updated project, or {script}; fall back to re-reading the project
    if (isObj(r) && (isObj(r.project) || "brief" in r)) return normProject(r);
    return httpApi.project(id);
  },
  async prices(id, sceneId, model) {
    const q = new URLSearchParams();
    if (sceneId) q.set("scene", sceneId);
    if (model) q.set("model", model);
    const tail = q.toString();
    return normPrices(await call("GET", `projects/${encodeURIComponent(id)}/prices${tail ? `?${tail}` : ""}`));
  },
  async estimate(id, mode) {
    const raw = await call("POST", `projects/${encodeURIComponent(id)}/estimate`, mode ?? {});
    return mode ? normProducePrice(raw) : normEstimate(raw);
  },

  async makeFrame(id, sceneId, p = {}) {
    return normJob(await call("POST", `${shot(id, sceneId)}/frame`, p));
  },
  async references(id, sceneId) {
    return normReferences(await call("GET", `${shot(id, sceneId)}/references`));
  },
  async lastFrame(id, sceneId, toSceneId) {
    return call("POST", `${shot(id, sceneId)}/last-frame`, toSceneId ? { toSceneId } : {});
  },
  async places(id) {
    return normPlaces(await call("GET", `projects/${encodeURIComponent(id)}/places`));
  },
  async addPlace(id, body) {
    const o = one(await call("POST", `projects/${encodeURIComponent(id)}/places`, body), "result");
    return { ...(isObj(o.job) ? { job: normJob(o.job) } : {}), ...(typeof o.credits === "number" ? { credits: o.credits } : {}), ...(o.place ? { place: normPlace(o.place, body.kind) ?? undefined } : {}) };
  },
  async patchPlace(id, kind, placeId, patch) {
    await call("PATCH", `projects/${encodeURIComponent(id)}/places/${kind}/${encodeURIComponent(placeId)}`, patch);
  },
  async setPlaceScenes(id, kind, placeId, scenes) {
    return normProject(one(await call("PUT", `projects/${encodeURIComponent(id)}/places/${kind}/${encodeURIComponent(placeId)}/scenes`, { scenes }), "result").project);
  },
  async removePlace(id, kind, placeId) {
    await call("DELETE", `projects/${encodeURIComponent(id)}/places/${kind}/${encodeURIComponent(placeId)}`);
  },
  async setClipEnd(id, sceneId, assetId) {
    const o = one(await call("PUT", `${shot(id, sceneId)}/clip-end`, { assetId }), "result");
    return { project: normProject(o.project), modelTakesIt: o.modelTakesIt === true, modelLabel: str(o.modelLabel), models: Array.isArray(o.models) ? o.models.map((x) => str(x)) : [] };
  },
  async addDocument(id, file, signal) {
    const token = authToken ? await authToken().catch(() => null) : null;
    const type = docMime(file);
    let r: Response;
    try {
      r = await fetch(`/api/projects/${encodeURIComponent(id)}/documents?name=${encodeURIComponent(file.name)}`, { method: "POST", credentials: "include", headers: { "content-type": type, ...(token ? { authorization: `Bearer ${token}` } : {}) }, body: file, signal });
    } catch (e) {
      if (signal?.aborted) throw cancelled();
      throw new ApiError((e as Error)?.message || "The upload didn't go through.", 0, null, null);
    }
    const text = await r.text();
    let data: unknown = undefined;
    try {
      data = text ? JSON.parse(text) : undefined;
    } catch {
      data = text;
    }
    if (!r.ok) throw new ApiError((isObj(data) && str(data.error ?? data.message)) || `The document couldn't be added (${r.status}).`, r.status, isObj(data) && data.code ? str(data.code) : null, isObj(data) ? (data.details ?? null) : null);
    const o = isObj(data) ? data : {};
    return { project: normProject(o.project), added: num(o.added), cut: o.cut === true, full: o.full === true, pages: typeof o.pages === "number" ? o.pages : null, preview: str(o.preview) };
  },
  async readWebsite(id, url, pictures = true) {
    const o = one(await call("POST", `projects/${encodeURIComponent(id)}/website`, { url, pictures }), "result");
    const pg = isObj(o.page) ? o.page : {};
    return {
      project: normProject(o.project),
      page: { url: str(pg.url), title: str(pg.title), description: str(pg.description), siteName: str(pg.siteName), themeColor: str(pg.themeColor) || null, words: num(pg.words) },
      added: num(o.added),
      cut: o.cut === true,
      full: o.full === true,
      images: Array.isArray(o.images) ? o.images.map(normAsset) : [],
      preview: str(o.preview),
    };
  },
  async setSceneFit(id, sceneId, fit) {
    const o = one(await call("PUT", `${shot(id, sceneId)}/fit`, { fit }), "result");
    const c = isObj(o.clip) ? o.clip : null;
    return {
      project: normProject(o.project),
      fit: o.fit === "video" || o.fit === "audio" ? o.fit : null,
      clip: c ? { durationS: typeof c.durationS === "number" ? c.durationS : null, approved: c.approved === true, lipSynced: c.lipSynced === true } : null,
      voiceS: typeof o.voiceS === "number" ? o.voiceS : null,
      speed: typeof o.speed === "number" ? o.speed : null,
      limited: o.limited === true,
    };
  },
  async setReferences(id, sceneId, body) {
    return normReferences(await call("PUT", `${shot(id, sceneId)}/references`, body));
  },
  async makeClip(id, sceneId, p = {}) {
    return normJob(await call("POST", `${shot(id, sceneId)}/clip`, p));
  },
  async setLipSync(id, sceneId, on) {
    await call("POST", `${shot(id, sceneId)}/lipsync`, { on });
  },
  async setSceneModel(id, sceneId, step, model) {
    await call("POST", `${shot(id, sceneId)}/model`, { step, model });
  },
  async rateLipSync(id, sceneId, verdict) {
    await call("POST", `${shot(id, sceneId)}/lipsync/rate`, { verdict });
  },
  async makeSounds(id, sceneId, expectCredits) {
    return normJob(await call("POST", `${shot(id, sceneId)}/sfx`, expectCredits !== undefined ? { expectCredits } : {}));
  },
  async makeMusic(id, p = {}) {
    return normJob(await call("POST", `projects/${encodeURIComponent(id)}/music`, p));
  },
  async approve(id, sceneId, what) {
    await call("POST", `${shot(id, sceneId)}/approve`, { what });
  },
  async factcheck(id) {
    const r = await call("GET", `projects/${encodeURIComponent(id)}/factcheck`);
    if (!isObj(r) || typeof r.summary !== "string") return null;
    return { checked: Number(r.checked) || 0, ok: r.ok === true, summary: r.summary, findings: (Array.isArray(r.findings) ? r.findings : []).filter(isObj).map((f) => ({ sceneN: Number(f.sceneN) || 0, sentence: str(f.sentence), status: (["sourced", "drift", "quote", "unsourced"].includes(str(f.status)) ? str(f.status) : "unsourced") as "sourced" | "drift" | "quote" | "unsourced", detail: str(f.detail) })) };
  },
  async publishKit(id, version) {
    return call("GET", `projects/${encodeURIComponent(id)}/publish-kit?v=${version}`);
  },
  async setLineSpeed(id, sceneId, key, speed) {
    const r = await call("PUT", `${shot(id, sceneId)}/lines/${encodeURIComponent(key)}/speed`, { speed });
    return normProject(isObj(r) ? r.project : r);
  },
  async scratch(id, sceneId, what) {
    const r = await call("POST", `${shot(id, sceneId)}/scratch`, { what });
    return { restored: isObj(r) && r.restored === true };
  },
  async reject(id, sceneId, what, note, expectCredits) {
    const r = await call("POST", `${shot(id, sceneId)}/reject`, { what, ...(note ? { note } : {}), ...(expectCredits !== undefined ? { expectCredits } : {}) });
    return isObj(r) && isObj(r.job) ? normJob(r.job) : null;
  },
  async approveMusic(id) {
    await call("POST", `projects/${encodeURIComponent(id)}/music/approve`, {});
  },
  async rejectMusic(id, note, expectCredits) {
    const r = await call("POST", `projects/${encodeURIComponent(id)}/music/reject`, { ...(note ? { note } : {}), ...(expectCredits !== undefined ? { expectCredits } : {}) });
    return isObj(r) && isObj(r.job) ? normJob(r.job) : null;
  },
  async sceneSoundPreview(id, sceneId, body = {}) {
    return (await call<{ preview: SceneSoundPreview }>("POST", `${shot(id, sceneId)}/sound-preview`, body)).preview;
  },
  async voicePrice(id, sceneIds) {
    return (await call<{ plan: VoicePlan }>("GET", `projects/${encodeURIComponent(id)}/voices/price${qs({ scenes: sceneIds?.length ? sceneIds.join(",") : undefined })}`)).plan;
  },
  async recordVoices(id, sceneIds, expectCredits) {
    const r = await call<{ plan: VoicePlan; jobs: unknown[] }>("POST", `projects/${encodeURIComponent(id)}/voices/record`, { ...(sceneIds?.length ? { scenes: sceneIds } : {}), expectCredits });
    return { plan: r.plan, jobs: (r.jobs ?? []).map(normJob) };
  },
  async playVoices(id, sceneIds) {
    return (await call<{ audio: VoiceAudio }>("POST", `projects/${encodeURIComponent(id)}/voices/play`, sceneIds?.length ? { scenes: sceneIds } : {})).audio;
  },
  async voicePass(id) {
    return (await call<{ preview: VoicePass }>("POST", `projects/${encodeURIComponent(id)}/voices/pass`, {})).preview;
  },
  async editLines(id, body) {
    const r = await call<{ project: unknown; clipsSetAside?: number[] }>("POST", `projects/${encodeURIComponent(id)}/script/lines`, body);
    return { project: normProject(r.project), clipsSetAside: r.clipsSetAside ?? [] };
  },
  async setLipLead(id, sceneId, seconds) {
    await call("POST", `${shot(id, sceneId)}/lipsync/lead`, { seconds });
  },
  async patchSfx(id, sceneId, sfx) {
    return normProject(await call("PATCH", shot(id, sceneId), { sfx }));
  },

  async models(step) {
    return list(await call("GET", `models${qs({ step })}`), "models").map(normModel);
  },
  async myModels() {
    const o = one(await call("GET", "me/models"), "models");
    return { defaults: stepMap(o.defaults), account: stepMap(o.account) };
  },
  async setMyModel(step, model) {
    await call("PATCH", "me/models", { step, model });
  },
  async setProjectModel(id, step, model) {
    await call("PATCH", `projects/${encodeURIComponent(id)}/models`, { step, model });
  },

  async versions(id) {
    return list(await call("GET", `projects/${encodeURIComponent(id)}/versions`), "versions").map(normVersion);
  },
  async projectFiles(id) {
    return normProjectFiles(await call("GET", `projects/${encodeURIComponent(id)}/files`));
  },
  async restoreVersion(id, n) {
    return normProject(await call("POST", `projects/${encodeURIComponent(id)}/versions/${n}/restore`, {}));
  },
  async setThumbnail(id, p) {
    const o = (await call<Record<string, unknown>>("POST", `projects/${encodeURIComponent(id)}/thumbnail`, { version: p.version ?? null, atS: p.atS })) ?? {};
    return { version: num(o.version), thumbUrl: (o.thumbUrl as string) ?? null, posterAtS: typeof o.posterAtS === "number" ? o.posterAtS : null, posterCustom: o.posterCustom === true };
  },

  async produce(id, mode) {
    const r = await call("POST", `projects/${encodeURIComponent(id)}/produce`, mode);
    if (isObj(r) && isObj(r.job)) return [normJob(r.job)];
    return list(r, "jobs").map(normJob);
  },
  async jobs(id) {
    return list(await call("GET", `projects/${encodeURIComponent(id)}/jobs`), "jobs").map(normJob);
  },
  async job(jobId) {
    return normJob(await call("GET", `jobs/${encodeURIComponent(jobId)}`));
  },
  async render(id, expectCredits) {
    return normRender(await call("POST", `projects/${encodeURIComponent(id)}/render`, { confirm: true, ...(expectCredits !== undefined ? { expectCredits } : {}) }));
  },
  async getRender(id) {
    return normRender(await call("GET", `renders/${encodeURIComponent(id)}`));
  },
  async projectRenders(id) {
    return list(await call("GET", `projects/${encodeURIComponent(id)}/renders`), "renders").map(normRender);
  },
  async nextStep(id) {
    return normNextStep(await call("GET", `projects/${encodeURIComponent(id)}/next`));
  },

  async library(q = {}) {
    const scope = q.scope && q.scope !== "account" ? q.scope : undefined;
    return list(
      await call("GET", `library${qs({ kind: q.kind || undefined, q: q.q, channelId: q.channelId, projectId: q.projectId, scope, here: q.here && (scope || q.scope) ? "1" : undefined, role: q.role, origin: q.origin })}`),
      "assets",
      "library",
      "items",
    ).map(normAsset);
  },
  async patchLibrary(id, patch) {
    return normAsset(await call("PATCH", `library/${encodeURIComponent(id)}`, patch));
  },
  async retagLibrary(id) {
    return normAsset(await call("POST", `library/${encodeURIComponent(id)}/retag`, {}));
  },
  async saveLibrary(p) {
    return normAsset(await call("POST", "library", p));
  },
  async uploadFile(file, opts, onProgress, signal) {
    const started = await call<Json>("POST", "uploads", { ...opts, name: file.name, mime: file.type, bytes: file.size });
    const id = str(started.uploadId);
    const put = isObj(started.put) ? started.put : null;
    if (signal?.aborted) throw cancelled();
    const viaApi = () => putBytes(`/api/${str(started.stream).replace(/^\/?api\//, "") || `uploads/${encodeURIComponent(id)}/data`}`, file, { "content-type": "application/octet-stream" }, true, onProgress, signal);
    if (put) {
      try {
        await putBytes(str(put.url), file, isObj(put.headers) ? (put.headers as Record<string, string>) : {}, false, onProgress, signal);
      } catch (e) {
        // A direct PUT the browser blocked (the bucket's CORS rule, a network filter) fails with status 0: send the same
        // bytes through our api instead (its streamed route takes any open upload). Real refusals and cancels stand.
        if (!(e instanceof ApiError) || e.status !== 0 || e.code === "CANCELLED" || signal?.aborted) throw e;
        onProgress?.(0);
        await viaApi();
      }
    } else await viaApi();
    if (signal?.aborted) throw cancelled();
    onProgress?.(1);
    return normAsset(await call("POST", `uploads/${encodeURIComponent(id)}/complete`));
  },
  async myStorage() {
    const o = one(await call("GET", "me/storage"), "storage");
    return {
      termsAccepted: o.termsAccepted === true,
      usedBytes: num(o.usedBytes),
      quotaBytes: o.quotaBytes == null ? null : num(o.quotaBytes),
      uploadsBytes: o.uploadsBytes == null ? undefined : num(o.uploadsBytes),
      generatedBytes: o.generatedBytes == null ? undefined : num(o.generatedBytes),
      byKind: isObj(o.byKind) ? Object.fromEntries(Object.entries(o.byKind).map(([k, v]) => [k, num(v)])) : undefined,
      limits: isObj(o.limits) ? (o.limits as StorageInfo["limits"]) : undefined,
    };
  },
  async sunoPack(id) {
    try {
      return normSuno(await call("GET", `projects/${encodeURIComponent(id)}/suno-pack`));
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }
  },
  async writeSunoPack(id, args = {}) {
    const o = one(await call("POST", `projects/${encodeURIComponent(id)}/suno-pack`, args), "result");
    const suno = normSuno(o.suno);
    if (!suno) throw new ApiError("The Suno pack didn't come back.", 500);
    return { suno, credits: num(o.credits), balance: typeof o.balance === "number" ? o.balance : null };
  },
  async patchSunoPack(id, patch) {
    const suno = normSuno(await call("PATCH", `projects/${encodeURIComponent(id)}/suno-pack`, patch));
    if (!suno) throw new ApiError("The Suno pack didn't come back.", 500);
    return suno;
  },

  async directorSession(ctx = {}) {
    // POST starts the session (its minutes row); GET is the api's read-only alias (review L8)
    const o = await call<Json>("POST", `director/session${qs({ projectId: ctx.projectId ?? undefined, tz: ctx.tz, text: ctx.text ? "1" : undefined })}`, {});
    return {
      conversationToken: str(pick(o, "conversationToken", "conversation_token", "token")) || null,
      signedUrl: str(pick(o, "signedUrl", "signed_url")) || null,
      agentId: str(pick(o, "agentId", "agent_id")),
      dynamicVariables: isObj(o.dynamicVariables) ? (o.dynamicVariables as DirectorSession["dynamicVariables"]) : {},
      dryRun: o.dryRun === true,
      voiceId: str(pick(o, "voiceId", "voice_id")) || null,
      sessionId: str(pick(o, "sessionId", "session_id")) || null,
      director: isObj(o.director) ? normPersona(o.director) : null,
      // v3: the call is charged per minute and may run for maxSeconds (what the balance and the daily limits allow)
      maxSeconds: o.maxSeconds === undefined ? null : num(o.maxSeconds) || null,
      holdSeconds: o.holdSeconds === undefined ? null : num(o.holdSeconds) || null,
      creditsPerMin: o.creditsPerMin === undefined ? null : num(o.creditsPerMin),
      charged: o.charged === undefined ? null : o.charged === true,
    };
  },
  async directorSessionConnect(p) {
    await call("POST", "director/session/connect", p);
  },
  async directorSessionTick(p) {
    const o = await call<Json>("POST", "director/session/tick", { sessionId: p.sessionId, seconds: Math.max(0, Math.round(p.seconds)) });
    return {
      secondsLeft: isObj(o) && typeof o.secondsLeft === "number" ? o.secondsLeft : null,
      balance: isObj(o) && o.balance !== undefined ? num(o.balance) : null,
      ended: isObj(o) && o.ended === true,
    };
  },
  async directorPersona() {
    return normPersona(one(await call("GET", "director/persona"), "persona"));
  },
  async setDirectorPersona(change) {
    const o = await call<Json>("PUT", "director/persona", change);
    return { persona: normPersona(one(o, "persona")), voiceChanged: o.voiceChanged === true, nameChanged: o.nameChanged === true };
  },
  async saveCarryover(summary) {
    await call("POST", "director/carryover", { summary });
  },
  async directorSessionEnd(p) {
    const o = await call<Json>("POST", "director/session/end", { sessionId: p.sessionId, ...(p.conversationId ? { conversationId: p.conversationId } : {}), seconds: Math.max(0, Math.round(p.seconds)), ...(p.error ? { error: p.error.slice(0, 500) } : {}) }, { keepalive: true });
    return { credits: num(o?.credits), balance: isObj(o) && o.balance !== undefined ? num(o.balance) : null };
  },
  async adminCosts(window) {
    return normCosts(await call("GET", `admin/costs${qs({ window })}`));
  },
  async accessRequests() {
    return list(await call("GET", "admin/access-requests"), "requests", "accessRequests", "users").map((v) => {
      const o = one(v, "user");
      return { userId: str(pick(o, "userId", "user_id", "id")), email: str(o.email), name: str(o.name), note: (o.note as string) ?? null, createdAt: str(pick(o, "createdAt", "created_at", "requestedAt")) || undefined };
    });
  },
  async approveAccess(userId) {
    await call("POST", `admin/access-requests/${encodeURIComponent(userId)}/approve`, {});
  },

  async invites() {
    return list(await call("GET", "admin/invites"), "invites").map(normInvite);
  },
  async createInvite(p) {
    const o = one(await call("POST", "admin/invites", p), "invite");
    return { code: str(o.code), url: str(o.url) };
  },
  async revokeInvite(code) {
    await call("POST", `admin/invites/${encodeURIComponent(code)}/revoke`, {});
  },
  async people(q = {}) {
    const r = await call("GET", `admin/people${qs({ q: q.q?.trim() || undefined, access: q.access })}`);
    const o = isObj(r) ? r : {};
    const c = isObj(o.counts) ? o.counts : {};
    return {
      people: list(r, "people").map(normPerson),
      counts: { pending: num(c.pending), active: num(c.active), suspended: num(c.suspended), total: num(c.total) },
      trialCredits: num(o.trialCredits, 750),
    };
  },
  async person(id) {
    const o = one(await call("GET", `admin/people/${encodeURIComponent(id)}`), "");
    return {
      person: normPerson(o.person),
      ledger: list(o.ledger).map((v) => {
        const l = isObj(v) ? v : {};
        return { id: str(l.id), delta: num(l.delta), reason: str(l.reason), ref: (l.ref as string) ?? null, note: (l.note as string) ?? null, by: (l.by as string) ?? null, createdAt: str(l.createdAt) };
      }),
    };
  },
  async approvePerson(id, p) {
    return normPerson(one(await call("POST", `admin/people/${encodeURIComponent(id)}/approve`, p), "").person);
  },
  async grantCredits(id, p) {
    const o = one(await call("POST", `admin/people/${encodeURIComponent(id)}/grant`, p), "");
    return { person: normPerson(o.person), replayed: o.replayed === true };
  },
  async suspendPerson(id) {
    return normPerson(one(await call("POST", `admin/people/${encodeURIComponent(id)}/suspend`, {}), "").person);
  },
  async restorePerson(id) {
    return normPerson(one(await call("POST", `admin/people/${encodeURIComponent(id)}/restore`, {}), "").person);
  },
  async directorTool(name, args, context) {
    const o = await call<Json>("POST", `director/tool/${encodeURIComponent(name)}`, { args, context: { projectId: context?.projectId ?? null, ...(context?.agent ? { agent: true } : {}), ...(context?.stage ? { stage: context.stage } : {}), ...(context?.report ? { report: context.report } : {}) } });
    const result = typeof o?.result === "string" ? o.result : JSON.stringify(o?.result ?? o ?? "");
    // a tool can answer with one directive or a list of them (v4: voice_sample sends a stage push + its sample card);
    // dropping lists left every multi-directive tap without its cards ("No sample came back for that voice.")
    return { result, ui: isObj(o?.ui) || Array.isArray(o?.ui) ? (o.ui as ToolResponse["ui"]) : undefined };
  },

  events(on, onState) {
    let es: EventSource | null = null;
    let closed = false;
    let retry = 1000;
    let timer = 0;
    const names = ["render", "job", "credits", "asset", "scene_image", "project"];
    const handle = (name: string) => (ev: MessageEvent) => {
      let data: unknown;
      try {
        data = JSON.parse(String(ev.data));
      } catch {
        return;
      }
      const e = normEvent(name, data);
      if (e) on(e);
    };
    const open = () => {
      if (closed) return;
      es = new EventSource("/api/events", { withCredentials: true });
      es.onopen = () => {
        retry = 1000;
        onState?.(true);
      };
      es.onmessage = handle("");
      for (const n of names) es.addEventListener(n, handle(n) as EventListener);
      es.onerror = () => {
        onState?.(false);
        // the browser retries on its own while CONNECTING; reopen ourselves once it gives up
        if (es?.readyState === EventSource.CLOSED) {
          es = null;
          timer = window.setTimeout(open, retry);
          retry = Math.min(retry * 2, 30_000);
        }
      };
    };
    open();
    return () => {
      closed = true;
      window.clearTimeout(timer);
      es?.close();
    };
  },
};

// ------------------------------------------------------------------ v3: export a version to other editors

export type ExportFormat = "pack" | "fcpxml" | "xmeml" | "otio" | "srt" | "vtt";

/** A short-lived signed link to a version's export (the browser downloads it natively), and its file name. */
export async function exportLink(renderId: string, format: ExportFormat): Promise<{ url: string; filename: string }> {
  const r = await call("POST", `renders/${encodeURIComponent(renderId)}/export-link`, { format });
  const o = isObj(r) ? r : {};
  return { url: str(o.url), filename: str(o.filename) };
}

// ------------------------------------------------------------------ the active implementation

let impl: Api = httpApi;
export function installApi(a: Api) {
  impl = a;
}
/** Always delegates to the installed implementation (real, or the mock under VITE_MOCK=1). */
export const api: Api = new Proxy({} as Api, {
  get: (_t, k: keyof Api) => {
    const v = impl[k];
    return typeof v === "function" ? (v as (...a: unknown[]) => unknown).bind(impl) : v;
  },
});


